import { For, Show, type Component, type JSX } from 'solid-js';
import { Panel } from '~ui/panel';
import { Heading, Text } from '~ui/text';

import { Table, type Column } from '~ui/table';
import { formatCurrency, formatNumber, formatPercent, perMonth } from '~/lib/format';
import { price } from '~data';
import type { Account } from '~/data/portfolios';
import { accountCash, accountIncome, accountValue, accounts, contributionLimit, contributionRoom, loading, resolveAccount, type ResolvedHolding } from '~/stores/portfolio-store';
import { adjustedCostBase, totalReturn, totalReturnPct } from '~/stores/acb-store';
import { focusAccount, focusedAccountId, isHoldingOpen, openAccountId, showHolding, toggleTransactions, unfocusAccount } from '~/stores/accounts-view-store';
import { Button } from '~ui/button';
import { ArrowLeftIcon } from '~ui/icons';
import AccountChart from '~/features/account-chart';
import { accountSeries } from '~/stores/account-series';
import AccountFacts from '~/features/account-facts';
import AccountExposure from '~/features/account-exposure';

const acb = (r: ResolvedHolding): number => adjustedCostBase(r.accountId, r.symbol, r.holding.bookCost);

const totalReturnOf = (r: ResolvedHolding): number | null => totalReturn(r.accountId, r.symbol, r.holding.bookCost, r.value);
const totalReturnPctOf = (r: ResolvedHolding): number | null => totalReturnPct(r.accountId, r.symbol, r.holding.bookCost, r.value);

/** Market value less the adjusted cost base. Null while the position is unpriced
    — the same rule the store's own figures follow. */
const gain = (r: ResolvedHolding): number | null => (r.value == null ? null : r.value - acb(r));

const COLUMNS: Column<ResolvedHolding>[] = [
    { id: 'symbol', header: 'Symbol', size: 0.8, cell: (r) => <Text weight='medium'>{r().symbol}</Text> },
    // Units and book cost are summed from the blotter, not typed here: record a
    // buy in Transactions and the position follows.
    { id: 'units', header: 'Units', align: 'right', size: 0.9, cell: (r) => formatNumber(r().holding.units, { maximumFractionDigits: 4 }) },
    { id: 'price', header: 'Price', align: 'right', size: 0.7, cell: (r) => formatCurrency(price(r().symbol)) },
    { id: 'value', header: 'Value', align: 'right', size: 0.9, cell: (r) => formatCurrency(r().value) },
    // Against the adjusted cost base, not the book cost: the RoC is cash already
    // in your pocket, so a gain measured against what you originally paid
    // understates the position by every dollar of it.
    { id: 'gain', header: 'Gain', align: 'right', size: 0.9, cell: (r) => <Text tone={gain(r()) == null ? undefined : gain(r())! >= 0 ? 'green' : 'red'}>{formatCurrency(gain(r()))}</Text> },
    // Received is here so the total return beside it can be checked by eye:
    // value + received - book is the whole of it; the parts are in the panel.
    {
        id: 'total',
        header: 'Total return',
        align: 'right',
        size: 1.2,
        cell: (r) => (
            <Text tone={totalReturnOf(r()) == null ? undefined : totalReturnOf(r())! >= 0 ? 'green' : 'red'}>
                {formatCurrency(totalReturnOf(r()))} · {formatPercent(totalReturnPctOf(r()), 1)}
            </Text>
        ),
    },
    { id: 'yield', header: 'Yield', align: 'right', size: 0.7, cell: (r) => formatPercent(r().yieldPct, 2) },
    // Beside the yield, not instead of it. Yield is what the fund pays on what a
    // unit costs today, and moves with the price; yield on cost is what it pays
    // on what you actually paid, and only moves when the distribution does. The
    // gap between the two is the whole story of a position bought well — and
    // either one alone reads as "the yield" and gets taken for the other.
    { id: 'yoc', header: 'YoC', align: 'right', size: 0.7, cell: (r) => <Text tone='muted'>{formatPercent(r().yieldOnCost, 2)}</Text> },
];

// An account is live if it holds something or has cash in it. A seeded account
// nobody has used yet is neither, and gets a grey dot and one row — the empty
// table it used to draw was a screenful of nothing per unused account, and there
// are three of them on a fresh book.
/** Live if it holds something or has cash in it. A seeded account nobody has
    used is neither, and does not need a card of its own. */
const isActive = (account: Account): boolean => resolveAccount(account).length > 0 || accountCash(account) !== 0;

/**
 * The bar's two filled bands, as percentages of the lifetime cap.
 *
 * The whole bar is the cap. What has been contributed fills it, and that fill
 * splits: money that is out working, and money sitting in the account doing
 * nothing. What is left is the track — room you still have.
 *
 * Idle comes off `accountCash`, so it counts distributions that have landed and
 * not been reinvested as well as deposits never spent. That is the honest
 * reading of "not working". It is capped at what was contributed, because the
 * band is a slice of the fill and cannot be wider than it.
 *
 * Null where there is no cap — an uncapped account has no fraction to draw.
 */
const bands = (a: Account): { allocated: number; idle: number } | null => {
    const limit = contributionLimit(a);
    if (limit == null || limit <= 0) return null;
    // Clamped: over-contributing fills the bar and is said in red rather than
    // drawn past the end of it.
    const contributed = Math.min(Math.max(0, a.deposit), limit);
    const idle = Math.min(Math.max(0, accountCash(a)), contributed);
    return { allocated: ((contributed - idle) / limit) * 100, idle: (idle / limit) * 100 };
};

/** One labelled figure in the card's header block. */
const Stat: Component<{ label: string; children: JSX.Element }> = (props) => (
    <span class='flex items-baseline gap-1 whitespace-nowrap'>
        <Text tone='muted'>{props.label}</Text>
        {props.children}
    </span>
);

// An account as a card, matching the Sandbox. Four side by side are four places;
// four full-width bands were a column of near-identical headers you had to read
// the label of to tell apart.
//
// Everything the band carried is still here, labelled — a row of bare figures is
// a row of guesses.
//
// Figures only: what it is worth, what it pays, what is unallocated and what room
// is left. The holdings are not on it. They were, as a count and a run of
// tickers, and that was the card half-answering a question the account's own page
// answers properly — enough to read, never enough to act on, and the one thing on
// the card you would have had to open the account to finish.
const AccountCard: Component<{ account: Account }> = (props) => {
    return (
        // The whole card is the target. Everything on it is a fact about one
        // account, so anywhere you point is already pointing at the thing you
        // want opened — a name-sized hit area on a card this big is a puzzle.
        //
        // Which is also why nothing in here is a control any more: the deposits
        // box and the transactions button moved to the account's own page,
        // where a click means what it says rather than competing with the card
        // underneath it.
        <button
            type='button'
            title={`Open ${props.account.label}`}
            onClick={() => focusAccount(props.account.id)}
            // Shorter now that the holdings are off it. At the old height the
            // card was two thirds empty space, which reads as something that
            // failed to load rather than as a card with four figures on it.
            class='flex min-h-32 min-w-0 cursor-default flex-col bg-background text-left hover:bg-muted'>
            {/* px-4 py-3, the same box the stat cards above use. Two card
                shapes on one page with different gutters read as two systems. */}
            <div class='flex shrink-0 items-start justify-between gap-3 py-3 pl-4'>
                <div class='flex min-w-0 flex-col items-start gap-1'>
                    {/* The same size as the value opposite it. They are the two
                        ends of one line — what this is, and what it comes to —
                        and a name a step larger than the figure made the pair
                        read as a heading over a detail rather than as a label
                        and its number. */}
                    <Heading level={1} class='block min-w-0 truncate text-lg tracking-tight'>
                        {props.account.label}
                    </Heading>
                    {/* Under the name and larger than it, because it is the
                        answer this whole app is for. It was a small green line
                        tucked under the market value, which put the number you
                        are building toward second to the number it is being
                        built out of.

                        Not green. Green is the app's mark for money coming in
                        against money going out, and on a card with nothing to
                        contrast it against it was decoration — size already says
                        this is the figure to read. */}
                    <Text weight='medium' class='block text-3xl tracking-tight whitespace-nowrap'>
                        {perMonth(accountIncome(props.account))}
                    </Text>
                    <div class='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                        {/* Deposited and not yet spent. "Cash" read as a balance
                            you meant to hold; this is money waiting to be put to
                            work, which is a different thing to look at. */}
                        <Stat label='Unallocated'>
                            <Text tone={accountCash(props.account) < 0 ? 'red' : undefined}>{formatCurrency(accountCash(props.account))}</Text>
                        </Stat>
                    </div>
                </div>

                <div class='flex shrink-0 flex-col items-end gap-1 whitespace-nowrap pr-4'>
                    <Text weight='medium' class='text-lg tracking-tight'>
                        {formatCurrency(accountValue(props.account))}
                    </Text>
                </div>
            </div>

            {/* Holds the foot down so every card's last line sits on the same
                baseline whatever is above it. */}
            <div class='min-h-0 flex-1' />

            {/* The foot: what is left of the allowance. Not a property of
                anything above it, which is why it sits apart from the figures
                under the name rather than among them — and it appears only on an
                account that has a cap to be measured against. */}
            <div class='flex w-full shrink-0 items-baseline justify-end gap-3 px-4 pb-2 whitespace-nowrap'>
                {/* Over-contributing a TFSA is penalised monthly, so a negative
                    shows in red rather than being clamped away. */}
                <Show when={contributionRoom(props.account) != null}>
                    <span class='flex items-baseline gap-1'>
                        <Text tone='muted'>Room</Text>
                        <Text tone={(contributionRoom(props.account) ?? 0) < 0 ? 'red' : undefined}>{formatCurrency(contributionRoom(props.account))}</Text>
                    </span>
                </Show>
            </div>

            {/* How full the account is, as the card's own bottom edge.

                Contributed against the lifetime cap — the one thing about a
                registered account that is a fraction of something fixed, and so
                the one thing on this card that can be a bar rather than a figure.
                The number above it says 56,983 left, which is a quantity you have
                to hold against a limit you have to remember; this says "about
                half" without being read.

                Inset to the card's own gutter rather than run edge to edge: the
                cards are separated by a 1px grid line, and a bar flush to the
                bottom sat on that seam and read as part of it — a rule between
                two cards rather than a figure belonging to this one. It lines up
                with the name and the room figure above it instead, which is what
                it is measuring. Only where there is a cap — an uncapped account
                has no fraction to draw. */}
            <Show when={bands(props.account)} keyed>
                {(band) => (
                    <div class='shrink-0 px-4 pb-3'>
                        {/* Square. Nothing else in this app has a rounded corner
                            — the panels, the tables and the cards are all right
                            angles — and a pill here would be the one soft shape
                            on screen.

                            Three zones, left to right in the order the money
                            moves: invested, idle, unused room. The track is the
                            last of them rather than a container the other two sit
                            in, which is why it is the element's own background. */}
                        <div
                            class='flex h-1 w-full bg-muted-foreground/20'
                            title={`${formatCurrency(props.account.deposit)} of ${formatCurrency(contributionLimit(props.account))} contributed · ${formatCurrency(accountCash(props.account))} unallocated`}>
                            {/* The app's blue slot, which has its own dark-mode
                                step and is already validated — not a new hue.
                                Red instead once past the cap, where the bar is
                                pinned full and stops being able to say how far
                                past; a TFSA over its limit is penalised monthly,
                                which is the one state here worth a colour. */}
                            <div
                                class={(contributionRoom(props.account) ?? 0) < 0 ? 'bg-red' : ''}
                                style={{ width: `${band.allocated.toFixed(2)}%`, background: (contributionRoom(props.account) ?? 0) < 0 ? undefined : 'var(--series-1)' }}
                            />
                            {/* Money in the account and not working. White in
                                both themes — literally white, not the surface
                                token, which is white in one and near-black in
                                the other. */}
                            <div class='bg-white' style={{ width: `${band.idle.toFixed(2)}%` }} />
                        </div>
                    </div>
                )}
            </Show>
        </button>
    );
};

/** One account with the page to itself: the same figures, plus the holdings
    table given room for every column rather than a card's worth. */
const FocusedAccount: Component<{ account: Account }> = (props) => {
    const holdings = () => resolveAccount(props.account);

    return (
        <>
            {/* Full width, above everything: the name and what the account
                comes to. Nothing else — the figures that used to sit here are
                all in the panel below, and a heading repeating the first rows
                of the thing directly under it is a heading you stop reading. */}
            <div class='flex shrink-0 items-center justify-between gap-3 border-b py-2 pl-3'>
                {/* The arrow is part of the title rather than a bar above it.
                    A whole row spent on one button, over a heading that is
                    already the thing you would click to leave, was a row. */}
                {/* `group` so the arrow can answer the whole control's hover
                    rather than only its own — the target is the title, and an
                    arrow that moves only when you are exactly on it is a hint
                    you have already stopped looking for. */}
                <button
                    type='button'
                    class='group flex min-w-0 cursor-default items-center gap-1 rounded pr-2'
                    title='Back to every account'
                    onClick={unfocusAccount}>
                    {/* Sized from here rather than by changing the icon: it is
                        in the shared package and every app draws it. The
                        descendant selector outranks the w-4/h-4 it ships with. */}
                    <span class='shrink-0 text-foreground transition-transform duration-150 group-hover:-translate-x-1 [&_svg]:size-5'>
                        <ArrowLeftIcon />
                    </span>
                    <Heading level={1} class='block min-w-0 truncate text-3xl tracking-tight'>
                        {props.account.label}
                    </Heading>
                </button>
                {/* The one action this screen has, up here with the name rather
                    than in a bar of its own at the foot. A whole row spent on a
                    single button is a row, and it sat below a table it had
                    nothing to do with — the blotter is a property of the
                    account, which is what the header is.

                    Left of the figures so those stay flush to the edge: they are
                    what the header is anchored on, and a button after them would
                    push the account's value off the corner it belongs in. */}
                <div class='flex shrink-0 items-center gap-3 pr-3'>
                    <Button
                        label='Transactions'
                        title={`Show the trades recorded in ${props.account.label}`}
                        class={openAccountId() === props.account.id ? 'bg-muted-foreground/20 border-b-3 border-foreground hover:bg-muted-foreground/20!' : ''}
                        onClick={() => toggleTransactions(props.account.id)}
                    />
                    <div class='flex flex-col items-end whitespace-nowrap'>
                        <Text weight='medium' class='text-xl tracking-tight'>
                            {formatCurrency(accountValue(props.account))}
                        </Text>
                        <Text tone='green'>{perMonth(accountIncome(props.account))}</Text>
                    </div>
                </div>
            </div>

            <div class='flex min-h-0 flex-1'>
            {/* The facts column, same shape the Fund page uses. Left, because it
                is the fixed reference the things beside it are read against. */}
            <div class='min-h-0 w-1/5 shrink-0 overflow-y-auto border-r scrollbar-hide'>
                <AccountFacts account={props.account} />
            </div>

            <div class='flex min-w-0 flex-1 flex-col'>
            {/* The account's own history, which the grid of cards has no room
                for and which is the thing the extra space is worth spending on:
                a table of positions says where the money is, and only this says
                whether it has been working. */}
            <div class='shrink-0 border-b'>
                <AccountChart points={accountSeries(props.account.id)} height='h-56' />
            </div>

            <div class='min-h-0 flex-1'>
                <Show
                    when={holdings().length > 0}
                    fallback={
                        <Text tone='muted' class='block px-3 py-3'>
                            Nothing here yet
                        </Text>
                    }>
                    <Table
                        columns={COLUMNS}
                        rows={holdings()}
                        onRowClick={(r) => showHolding(props.account.id, r.symbol)}
                        isSelected={(r) => isHoldingOpen(props.account.id, r.symbol)}
                    />
                </Show>
            </div>

            {/* What the positions above resolve to, looked through. The table
                says you own two funds; this says they are the same five banks
                twice, which is the fact a two-fund account most often hides.

                A fixed band rather than a share of the space: the table is the
                thing that grows with the account, and letting three lists of
                sectors take half the pane on a four-holding TFSA would spend the
                room on the smaller answer. */}
            <Show when={holdings().length > 0}>
                <div class='flex h-56 shrink-0 flex-col border-t'>
                    <AccountExposure accountId={props.account.id} />
                </div>
            </Show>
            </div>
            </div>
        </>
    );
};

/** Cells the grid actually renders. */
const cardCount = (): number => accounts().filter(isActive).length;

const Accounts: Component = () => (
    <Panel>
        <Show when={accounts().find((a) => a.id === focusedAccountId())} keyed>
            {(account) => <FocusedAccount account={account} />}
        </Show>
        <Show when={focusedAccountId() == null}>
        <div class='flex-1 min-h-0 overflow-y-auto scrollbar-hide'>
            <Show
                when={accounts().length > 0}
                fallback={
                    <div class='flex h-16 items-center justify-center'>
                        <Text tone='muted'>{loading() ? 'Loading…' : 'No accounts'}</Text>
                    </div>
                }>
                <div class='grid grid-cols-2 gap-px border-b bg-border'>
                    <For each={accounts().filter(isActive)}>{(account) => <AccountCard account={account} />}</For>
                    {/* Filler for the odd cell.

                        The grid's own background is the seam colour and `gap-px`
                        lets it through between the cards. Where a row is half
                        full there is no card to stop at, so that colour fills the
                        whole missing cell and reads as a grey panel someone meant
                        to put something in. */}
                    <Show when={cardCount() % 2 === 1}>
                        <div class='bg-background' />
                    </Show>

                    {/* The unused accounts are not on this page.

                        They shared a slot here — a card listing the ones you have
                        not put anything in, each a button that opened its blotter.
                        It was the only thing on the page that was not a position,
                        and it took a whole cell of a two-column grid to offer
                        something nobody arrives here wanting.

                        They are not gone, only unlisted: the four kinds are still
                        fixed, an account still becomes live the moment a trade
                        lands in it, and nothing about that is stored. What is
                        missing is a way in from here, which is the thing to
                        design rather than to leave a placeholder standing in
                        for. */}
                </div>
            </Show>
        </div>
        </Show>
    </Panel>
);

export default Accounts;
